import { Injectable } from '@angular/core';
import { SolEstimatePricingMode } from '../../models/enum';
import { SellPriceConfig, TaxTypes } from '../../models/sol-estimate-table/SellPriceConfig';
import { EstimateCommonFunctionsService } from './estimate-common-functions.service';
import { EstimateUiModel, TAX_RATE_ROW_NAMES } from '../../models/sol-estimate-table/ui-models/index';
import { CONSTANTS } from '../../Constants/Constants';
import { roundHalfEven9 } from '../../../types/number-extensions';
import { SolEstimateTaxService } from './sol-estimate-tax.service';

/**
 * Handles the Sell-As section of the estimate tab.
 * Works exclusively with EstimateUiModel — no FormGroup references.
 */
@Injectable({
  providedIn: 'root'
})
export class SolEstimateSellAsService {

  constructor(
    private readonly estimateCommonFunctions: EstimateCommonFunctionsService,
    private readonly taxService: SolEstimateTaxService
  ) { }

  // ── Sell-As Sell Price Calculation ─────────────────────────────

  recalculateSellAsSellPrice(
    model: EstimateUiModel,
    config: SellPriceConfig
  ): void {
    const subTotalsSP = model.subTotals.sellPrice;
    const salesTaxSP = model.taxMaterialLaborRate.headerRow.sellPrice;
    const totalSellPrice = subTotalsSP + salesTaxSP;

    // In Fixed Price mode the sell price is user-controlled and must NOT
    // be overwritten by the computed total (e.g. when costs go to zero).
    if (config.isFixedPriceSelected !== true) {
      model.sellAs.sellAsSellPrice = totalSellPrice;
    }

    const effectiveSellPrice = model.sellAs.sellAsSellPrice;
    model.sellAs.sellAsPricingMode = this.resolvePricingMode(config.isFixedPriceSelected);

    if (config.isFixedPriceSelected === false) {
      const pair = this.estimateCommonFunctions.resolvePercentPair(config.soldAsGMPercent, config.isMarkUpSelected);
      model.sellAs.calculated.grossMarginPercentage = pair.grossMarginPercentage;
      model.sellAs.calculated.markUpPercentage = pair.markUpPercentage;
    } else {
      this.calculateCalculatedGM(model, config, effectiveSellPrice);
    }
    this.calculateReportedGM(model, config, effectiveSellPrice);
  }

  // ── Calculated GM% (SoldASGMPercent) ─────────────────────────

  /**
   * Computes the Calculated GM% and MU% for the Sell-As section.
   *
   * Unified formula (tax variables are conditionally populated):
   *   rawGM = 1 − {(SubTotalCost − UseTax − TechFeeCost)
   *               + (MatCost + MiscCost + MechanicalFee + RiskCost) × MaterialRate
   *               + (LaborCost + SubCost + TravelCost) × LaborRate}
   *             / (SellPrice − UseTax − TechFeeSP)
   *
   *   • UseTax / HST / PST:  UseTax = taxMaterialRate.headerRow.sellPrice; rates = 0
   *   • SalesTax / BOTax (Fixed Price/Margin): UseTax = 0; rates from taxRateRows
   *   • SalesTax / BOTax (Line Item): UseTax = 0; rates = 0; SellPrice = SubTotalSellPrice
   *   • NoTax / default: all tax variables = 0
   */
  private calculateCalculatedGM(
    model: EstimateUiModel,
    config: SellPriceConfig,
    totalSellPrice: number
  ): void {
    const calculatedGM = this.getCalculatedGMValues(model, config, totalSellPrice);
    model.sellAs.calculated.grossMarginPercentage = calculatedGM.grossMarginPercentage;
    model.sellAs.calculated.markUpPercentage = calculatedGM.markUpPercentage;
  }

  getCalculatedGMValues(
    model: EstimateUiModel,
    config: SellPriceConfig,
    totalSellPrice: number
  ): { grossMarginPercentage: number; markUpPercentage: number } {

    const subTotalsCost = model.subTotals.cost;
    const techFeeCost = model.technologyFee.cost;
    const techFeeSP = model.technologyFee.sellPrice;

    // ── Edge Case: CostPrice is zero → keep the configured (metadata) margin ──
    if (subTotalsCost === 0) {
      return this.estimateCommonFunctions.resolvePercentPair(config.soldAsGMPercent, config.isMarkUpSelected);
    }

    // ── Edge Case: SellPrice is zero → GM% = 0, MU% = −100 ──
    if (totalSellPrice === 0) {
      return {
        grossMarginPercentage: CONSTANTS.ZERO,
        markUpPercentage: CONSTANTS.NEGATIVE_ONE * CONSTANTS.HUNDRED_POINT_ZERO
      };
    }

    const { useTax, materialRate, laborRate, sellPriceBase } = this.getCalculatedGMTaxValues(
      model,
      config,
      totalSellPrice
    );

    const miscRows = model.miscRiskTravelCosts.rows;
    const riskRows = model.miscRiskTravelCosts.riskCosts?.rows ?? null;
    const findRow = (name: string) => this.estimateCommonFunctions.findNamedRowInModel(miscRows, riskRows, name);

    let materialBaseCost: number;
    let laborBaseCost: number;
    if (config.taxTypeID === TaxTypes.SalesTax) {
      // Same bases the tax amount uses, or GM% drifts on every pricing-mode switch.
      const bases = this.taxService.collectSalesTaxBases(model, 'cost');
      materialBaseCost = bases.materialBase;
      laborBaseCost = bases.laborBase;
    } else {
      const matCost = model.materialCosts.materialCostsHeader.cost;
      const miscCost = findRow('MISCELLANEOUS_COSTS')?.cost ?? 0;
      const mechanicalFee = findRow('MECHANICAL_FEE')?.cost ?? 0;
      const riskCost = model.miscRiskTravelCosts.riskCosts?.headerRow.cost ?? 0;

      const laborCost = model.laborCosts.laborCostsHeader.cost;
      const subCost = model.subContractorCosts.headerRow.cost;
      const travelCost = findRow('TRAVEL_EXPENSE_COSTS')?.cost ?? 0;

      materialBaseCost = matCost + miscCost + mechanicalFee + riskCost;
      laborBaseCost = laborCost + subCost + travelCost;
    }

    // GM% = 1 - {(SubTotalCost − UseTax − TechFeeCost) + MaterialBase×MatRate + LaborBase×LaborRate}
    //            / (SellPrice − UseTax − TechFeeSP)
    const adjustedCost = (subTotalsCost - useTax - techFeeCost)
      + materialBaseCost * materialRate
      + laborBaseCost * laborRate;

    const denominator = sellPriceBase - useTax - techFeeSP;
    const rawGM = denominator === 0 ? 0 : 1 - adjustedCost / denominator;

    // ── Step B: Convert rawGM to display percentages ──
    let gmPercent = roundHalfEven9(rawGM * 100);
    let muPercent = (1 - rawGM) === 0
      ? 0
      : roundHalfEven9((rawGM / (1 - rawGM)) * 100);

    // ── Edge Case: snap near-zero negatives to 0 ──
    if (gmPercent > -0.0001 && gmPercent < 0) { gmPercent = 0; }
    if (muPercent > -0.0001 && muPercent < 0) { muPercent = 0; }

    return {
      grossMarginPercentage: gmPercent,
      markUpPercentage: muPercent
    };
  }

  private getCalculatedGMTaxValues(
    model: EstimateUiModel,
    config: SellPriceConfig,
    totalSellPrice: number
  ): { useTax: number; materialRate: number; laborRate: number; sellPriceBase: number } {
    let useTax = CONSTANTS.ZERO;
    let materialRate = CONSTANTS.ZERO;
    let laborRate = CONSTANTS.ZERO;
    let sellPriceBase = totalSellPrice;

    switch (config.taxTypeID) {
      case TaxTypes.UseTax:
      case TaxTypes.HST:
      case TaxTypes.PST:
        useTax = model.taxMaterialRate.headerRow.sellPrice;
        break;
      case TaxTypes.SalesTax:
      case TaxTypes.BOTax: {
        const taxRateRows = model.taxMaterialLaborRate?.taxRateRows;
        if (config.isFixedPriceSelected === null) {
          sellPriceBase = model.subTotals.sellPrice;
        } else {
          const findRate = (name: string) =>
            (taxRateRows?.find(row => row.name === name)?.rate ?? CONSTANTS.ZERO) / CONSTANTS.HUNDRED_POINT_ZERO;
          materialRate = findRate(TAX_RATE_ROW_NAMES.material);
          laborRate = findRate(TAX_RATE_ROW_NAMES.labor);
        }
        break;
      }
    }

    return { useTax, materialRate, laborRate, sellPriceBase };
  }

  // ── Reported (Turbo) GM% (SoldASReportedGMPercent) ──────────────

  /**
   * Computes the Reported GM% and MU% for the Sell-As section.
   *
   * Unlike Calculated GM%, Technology Fee is **NOT** excluded here —
   * it remains part of both SubTotalsCost and SellPrice.
   *
   * Step A — Compute the adjusted sell price.
   *   For Sales Tax or BO Tax: adjustedSP = SellPrice − (MaterialTaxSP + LaborTaxSP)
   *   For all other tax types:  adjustedSP = SellPrice (unchanged)
   *   rawTurboGM = 1 − SubTotalsCost / adjustedSP
   *
   * Step B — Convert to display percentages.
   *   GM%  = rawTurboGM × 100
   *   MU%  = (rawTurboGM / (1 − rawTurboGM)) × 100
   *   If rawTurboGM == 1 in MU mode → MU% = 0 (avoid division by zero).
   *
   * Edge case — If CostPrice == 0 or SellPrice == 0, falls back to
   *   copying Calculated values directly into Reported.
   */
  private calculateReportedGM(
    model: EstimateUiModel,
    config: SellPriceConfig,
    soldAsSellPrice: number
  ): void {
    const costPrice = model.subTotals.cost;

    // Edge case: fall back when cost or sell price is zero
    if (costPrice === 0 || soldAsSellPrice === 0) {
      this.calculateReportedGMFromSoldAsGM(model);
      return;
    }

    // Step A: Compute adjusted sell price.
    // Sales Tax and BO Tax sell prices are subtracted from sell price side only.
    let adjustedSellPrice = soldAsSellPrice;
    if (config.taxTypeID === TaxTypes.SalesTax || config.taxTypeID === TaxTypes.BOTax) {
      const taxSellPrice = model.taxMaterialLaborRate.headerRow.sellPrice;
      adjustedSellPrice = soldAsSellPrice - taxSellPrice;
    }

    const rawTurboGM = 1 - (costPrice / adjustedSellPrice);

    // Step B: Convert to display percentages
    const reportedGMPercent = roundHalfEven9(rawTurboGM * 100);
    const reportedMUPercent = rawTurboGM === 1
      ? 0
      : roundHalfEven9((rawTurboGM / (1 - rawTurboGM)) * 100);
    const turboGMPercent = reportedGMPercent;

    // Snap near-zero negatives to 0 (consistent with calculated GM%)
    model.sellAs.turboGMPercent = (turboGMPercent > -0.0001 && turboGMPercent < 0) ? 0 : turboGMPercent;
    model.sellAs.reported.grossMarginPercentage = (reportedGMPercent > -0.0001 && reportedGMPercent < 0) ? 0 : reportedGMPercent;
    model.sellAs.reported.markUpPercentage = (reportedMUPercent > -0.0001 && reportedMUPercent < 0) ? 0 : reportedMUPercent;
  }

  /**
   * Fallback when SubTotalsCost or SoldASSellPrice is zero.
   * The reported GM/MU% mirrors the calculated values directly.
   */
  private calculateReportedGMFromSoldAsGM(model: EstimateUiModel): void {
    model.sellAs.reported.grossMarginPercentage = model.sellAs.calculated.grossMarginPercentage;
    model.sellAs.reported.markUpPercentage = model.sellAs.calculated.markUpPercentage;
  }

  // ── Pricing Mode Resolution ────────────────────────────────────

  private resolvePricingMode(isFixedPriceSelected: boolean | null): SolEstimatePricingMode {
    if (isFixedPriceSelected === true) {
      return SolEstimatePricingMode.Fixed_Price;
    }
    if (isFixedPriceSelected === false) {
      return SolEstimatePricingMode.Fixed_Margin;
    }
    return SolEstimatePricingMode.Line_Item;
  }

}
